/**
 * The assistant's reply text, read as a SAFE subset of Markdown (owner,
 * 2026-10-12: "we should also render markdown"). This replaces agent-1-web
 * §1 / criterion 5's "verbatim as text -- no markdown": the model now writes
 * conversational prose and sometimes bold, italics and a short list.
 *
 * This module only PARSES, into a small tree of plain data; the component
 * (`components/assistant/ReplyText.tsx`) turns the tree into React elements.
 * Nothing here, or there, ever produces HTML from the text, so every character
 * the model writes reaches the screen as text, escaped by React.
 *
 * What is understood:
 *
 * - paragraphs, separated by a blank line; a single newline is a line break;
 * - `**bold**` / `__bold__`, `*italic*` / `_italic_` (an `_` inside a word, as
 *   in `snake_case`, stays literal);
 * - `- `, `* ` and `+ ` bullet lists, `1. ` / `1) ` numbered lists (nested
 *   items are flattened into their list);
 * - inline `code`, and a ``` fenced block as plain preformatted text;
 * - a `#` heading, as a bold paragraph -- never a heading element, which would
 *   break the page's outline;
 * - `\*` and friends, the literal character.
 *
 * What is deliberately NOT: raw HTML (shown as typed, `<script>` and all),
 * links (`[text](url)` shows its text, never an anchor, and the URL is dropped),
 * images (`![alt](src)` shows its alt text), tables, block quotes (shown as
 * typed). An unmatched `*` or `**` is just a character.
 */

export type Inline =
  | { kind: 'text'; text: string }
  | { kind: 'strong'; children: Inline[] }
  | { kind: 'em'; children: Inline[] }
  | { kind: 'code'; text: string }
  | { kind: 'br' }

export type Block =
  | { kind: 'p'; children: Inline[] }
  | { kind: 'heading'; children: Inline[] }
  | { kind: 'ul'; items: Inline[][] }
  | { kind: 'ol'; start: number; items: Inline[][] }
  | { kind: 'pre'; text: string }

const FENCE = /^ {0,3}(`{3,}|~{3,})/
const HEADING = /^ {0,3}#{1,6}(?:\s+(.*?))?(?:\s+#+)?\s*$/
const RULE = /^ {0,3}([-*_])(?:\s*\1){2,}\s*$/
const BULLET = /^\s*[-*+]\s+(.*)$/
const ORDERED = /^\s*(\d{1,9})[.)]\s+(.*)$/
const ESCAPABLE = /[\\`*_{}[\]()#+\-.!<>~|]/

/** The reply's text as blocks. */
export function parseMarkdown(source: string): Block[] {
  const lines = source.replace(/\r\n?/g, '\n').split('\n')
  const blocks: Block[] = []
  /** The lines of the paragraph being gathered, or of the list item. */
  let para: string[] | null = null
  let list: { kind: 'ul' | 'ol'; start: number; items: string[][] } | null = null
  /** A blank line was seen inside a list: the next same-kind item continues it, anything else ends it. */
  let gap = false

  const flushPara = () => {
    if (para) blocks.push({ kind: 'p', children: inlineLines(para) })
    para = null
  }
  const flushList = () => {
    if (list) {
      const items = list.items.map(inlineLines)
      blocks.push(list.kind === 'ul' ? { kind: 'ul', items } : { kind: 'ol', start: list.start, items })
    }
    list = null
    gap = false
  }
  const flush = () => {
    flushPara()
    flushList()
  }

  for (let i = 0; i < lines.length; i += 1) {
    const line = lines[i]

    const fence = FENCE.exec(line)
    if (fence) {
      flush()
      const marker = fence[1]
      const body: string[] = []
      i += 1
      while (i < lines.length && !closesFence(lines[i], marker)) {
        body.push(lines[i])
        i += 1
      }
      blocks.push({ kind: 'pre', text: body.join('\n') })
      continue
    }

    if (line.trim() === '') {
      flushPara()
      if (list) gap = true
      continue
    }

    if (RULE.test(line)) {
      flush()
      continue
    }

    const heading = HEADING.exec(line)
    if (heading) {
      flush()
      if (heading[1]) blocks.push({ kind: 'heading', children: parseInline(heading[1]) })
      continue
    }

    const bullet = BULLET.exec(line)
    let ordered = bullet ? null : ORDERED.exec(line)
    // As in CommonMark, only a "1." interrupts a paragraph: "2026. was a year" is prose.
    if (ordered && para && ordered[1] !== '1') ordered = null
    if (bullet || ordered) {
      flushPara()
      const kind = bullet ? 'ul' : 'ol'
      if (list && list.kind !== kind) flushList()
      if (!list) list = { kind, start: ordered ? Number(ordered[1]) : 1, items: [] }
      list.items.push([bullet ? bullet[1] : ordered![2]])
      gap = false
      continue
    }

    // A line after a list item, with no blank line between, belongs to it.
    if (list && !gap) {
      list.items[list.items.length - 1].push(line)
      continue
    }
    flushList()
    if (para) para.push(line)
    else para = [line]
  }
  flush()
  return blocks
}

function closesFence(line: string, marker: string): boolean {
  const trimmed = line.trim()
  return trimmed.length >= marker.length && trimmed === marker[0].repeat(trimmed.length)
}

/** Lines of one paragraph or item: each parsed on its own, a line break between. */
function inlineLines(lines: string[]): Inline[] {
  const out: Inline[] = []
  lines.forEach((line, i) => {
    if (i !== 0) out.push({ kind: 'br' })
    out.push(...parseInline(line.trim()))
  })
  return mergeText(out)
}

/** One line's inline marks. */
export function parseInline(s: string): Inline[] {
  const out: Inline[] = []
  let text = ''
  const pushText = () => {
    if (text) out.push({ kind: 'text', text })
    text = ''
  }

  let i = 0
  while (i < s.length) {
    const c = s[i]

    if (c === '\\' && i + 1 < s.length && ESCAPABLE.test(s[i + 1])) {
      text += s[i + 1]
      i += 2
      continue
    }

    if (c === '`') {
      const span = codeSpan(s, i)
      if (span) {
        pushText()
        out.push({ kind: 'code', text: span.text })
        i = span.end
        continue
      }
      const run = runLength(s, i, '`')
      text += s.slice(i, i + run)
      i += run
      continue
    }

    // `[text](url)` shows its text; `![alt](src)` its alt text. Never a link or an image.
    if (c === '[' || (c === '!' && s[i + 1] === '[')) {
      const link = linkAt(s, c === '!' ? i + 1 : i)
      if (link) {
        pushText()
        out.push(...parseInline(link.label))
        i = link.end
        continue
      }
    }

    if (c === '*' || c === '_') {
      const run = runLength(s, i, c)
      const width = run >= 2 ? 2 : 1
      const close = opens(s, i, run, c) ? findCloser(s, i + width, c, width) : -1
      if (close !== -1) {
        pushText()
        const children = parseInline(s.slice(i + width, close))
        out.push(width === 2 ? { kind: 'strong', children } : { kind: 'em', children })
        i = close + width
        continue
      }
      text += s.slice(i, i + run)
      i += run
      continue
    }

    text += c
    i += 1
  }
  pushText()
  return out
}

function runLength(s: string, at: number, c: string): number {
  let n = 0
  while (s[at + n] === c) n += 1
  return n
}

const isSpace = (c: string | undefined) => c === undefined || /\s/.test(c)
const isWordChar = (c: string | undefined) => c !== undefined && /[\p{L}\p{N}]/u.test(c)

/** A delimiter run can open when the next character is not a space (and, for `_`, not mid-word). */
function opens(s: string, at: number, run: number, c: string): boolean {
  if (isSpace(s[at + run])) return false
  return c !== '_' || !isWordChar(s[at - 1])
}

/**
 * Where the delimiter that closes one opened at `from` is: `width` of `c`,
 * after a non-space, and (for `_`) not mid-word. Code spans, links and
 * escapes are stepped over; for a single delimiter, a doubled one is a nested
 * strong and is stepped over whole. -1 when nothing closes it.
 */
function findCloser(s: string, from: number, c: string, width: number): number {
  let j = from
  while (j < s.length) {
    const ch = s[j]
    if (ch === '\\') {
      j += 2
      continue
    }
    if (ch === '`') {
      const span = codeSpan(s, j)
      j = span ? span.end : j + runLength(s, j, '`')
      continue
    }
    if (ch === c) {
      const run = runLength(s, j, c)
      const canClose = j > from && !isSpace(s[j - 1]) && (c !== '_' || !isWordChar(s[j + run]))
      if (width === 2 && run >= 2 && canClose) return j + run - 2
      if (width === 1 && run === 1 && canClose) return j
      if (width === 1 && run >= 3 && canClose) return j + run - 1
      if (width === 1 && run === 2 && opens(s, j, run, c)) {
        // A nested strong: step over it to its own closer.
        const inner = findCloser(s, j + 2, c, 2)
        if (inner !== -1) {
          j = inner + 2
          continue
        }
      }
      j += run
      continue
    }
    j += 1
  }
  return -1
}

/** A code span opening at `at`: the same number of backticks closes it. */
function codeSpan(s: string, at: number): { text: string; end: number } | null {
  const run = runLength(s, at, '`')
  let j = at + run
  while (j < s.length) {
    if (s[j] === '`') {
      const close = runLength(s, j, '`')
      if (close === run) {
        let text = s.slice(at + run, j)
        if (text.length > 1 && text.startsWith(' ') && text.endsWith(' ') && text.trim() !== '') text = text.slice(1, -1)
        return { text, end: j + close }
      }
      j += close
      continue
    }
    j += 1
  }
  return null
}

/** `[label](anything)` at `at`, with balanced brackets in the label; the target is discarded. */
function linkAt(s: string, at: number): { label: string; end: number } | null {
  let depth = 0
  let j = at
  for (; j < s.length; j += 1) {
    if (s[j] === '\\') {
      j += 1
      continue
    }
    if (s[j] === '[') depth += 1
    else if (s[j] === ']') {
      depth -= 1
      if (depth === 0) break
    }
  }
  if (j >= s.length || s[j + 1] !== '(') return null
  const close = s.indexOf(')', j + 2)
  if (close === -1) return null
  return { label: s.slice(at + 1, j), end: close + 1 }
}

function mergeText(parts: Inline[]): Inline[] {
  const out: Inline[] = []
  for (const part of parts) {
    const last = out.at(-1)
    if (part.kind === 'text' && last?.kind === 'text') out[out.length - 1] = { kind: 'text', text: last.text + part.text }
    else out.push(part)
  }
  return out
}

/** The words alone, for the screen reader's live region: no marks, one line per paragraph or item. */
export function plainText(source: string): string {
  return parseMarkdown(source)
    .map((block) => {
      switch (block.kind) {
        case 'p':
        case 'heading':
          return inlineText(block.children)
        case 'ul':
          return block.items.map(inlineText).join('\n')
        case 'ol':
          return block.items.map((item, n) => `${block.start + n}. ${inlineText(item)}`).join('\n')
        case 'pre':
          return block.text
      }
    })
    .join('\n\n')
}

function inlineText(parts: Inline[]): string {
  return parts
    .map((part) => {
      switch (part.kind) {
        case 'text':
        case 'code':
          return part.text
        case 'br':
          return '\n'
        case 'strong':
        case 'em':
          return inlineText(part.children)
      }
    })
    .join('')
}
